import { useEffect, useState } from "react";
import { buildCatalogTree, CatalogNode } from "@equipped/core/content/catalogTree";
import { loadCatalogFromUrl } from "@equipped/core/content/catalogLoader";
import { rootCatalogUrl } from "./contentBase";

/**
 * Loads the published catalog tree so the Invites grant picker + Catalogs access roster have real
 * packs/catalogs to work with — mirrors `apps/admin/ui/useCatalogTree.ts` / `apps/admin-web/src/lib/
 * useCatalogTree.ts` (same one-shot fetch of the real published root, same `entryUrl` stamp, same
 * `includeHidden: true` so the admin sees private catalogs too).
 *
 * **Fixed (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3):** the original version swallowed a fetch
 * failure into an empty tree (`res.ok ? [...] : []`), so `CatalogsPage`/`InvitesPage`/`GrantPicker`
 * — which all treat "no targets yet" as "still loading" — showed a spinner forever instead of an
 * error, with no way to distinguish "loading," "failed," and "genuinely empty" (the same catalog
 * really has zero packs). Now returns a `{ tree, loading, error }` state instead of a bare
 * `CatalogNode | null` so a caller CAN tell those three apart; a thrown fetch (e.g. no network,
 * not just a parse failure) is caught too, since `loadCatalogFromUrl`'s own `fetch` isn't
 * try/catch-wrapped internally.
 */
export interface CatalogTreeState {
  tree: CatalogNode | null;
  loading: boolean;
  error: string | null;
}

const NOT_CONFIGURED: CatalogTreeState = { tree: null, loading: false, error: null };

export function useCatalogTree(backendUrl: string | null): CatalogTreeState {
  const catalogUrl = rootCatalogUrl(backendUrl);
  const [state, setState] = useState<CatalogTreeState>(
    catalogUrl ? { tree: null, loading: true, error: null } : NOT_CONFIGURED,
  );
  useEffect(() => {
    let active = true;
    if (!catalogUrl) {
      setState(NOT_CONFIGURED);
      return;
    }
    setState({ tree: null, loading: true, error: null });
    (async () => {
      try {
        const res = await loadCatalogFromUrl(catalogUrl);
        if (!active) return;
        if (!res.ok) {
          setState({ tree: null, loading: false, error: res.errors.join("; ") || "Couldn't load the catalog tree." });
          return;
        }
        const roots = [{ ...res.value, entryUrl: catalogUrl }];
        setState({ tree: buildCatalogTree(roots, undefined, true), loading: false, error: null });
      } catch (e) {
        if (!active) return;
        setState({ tree: null, loading: false, error: (e as Error).message || "Couldn't load the catalog tree." });
      }
    })();
    return () => {
      active = false;
    };
  }, [catalogUrl]);
  return state;
}
